import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

interface Tenant {
  slug: string;
  displayName: string;
  status: string;
}

interface EventItem {
  eventType: string;
  receivedAt: string;
}

const RECIPIENT_OPTIONS = [
  'success@simulator.amazonses.com',
  'bounce@simulator.amazonses.com',
  'complaint@simulator.amazonses.com',
  'suppressionlist@simulator.amazonses.com',
] as const;

const EVENT_COLORS: Record<string, string> = {
  Send: 'bg-indigo-50 text-indigo-700',
  Delivery: 'bg-emerald-50 text-emerald-700',
  Bounce: 'bg-red-50 text-red-700',
  Complaint: 'bg-amber-50 text-amber-700',
};

const inputClass =
  'w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20';

export function Send() {
  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const data = await apiFetch('/tenants');
      return ((data.items ?? []) as Tenant[]).filter((tenant) => tenant.status === 'active');
    },
  });
  const sendMutation = useMutation({
    mutationFn: async (input: { slug: string; to: string; subject: string; body: string }) =>
      apiFetch('/send-test', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }) as Promise<{ messageId: string }>,
  });

  const tenants = tenantsQuery.data ?? [];
  const [slug, setSlug] = useState('');
  const [recipientChoice, setRecipientChoice] = useState<(typeof RECIPIENT_OPTIONS)[number] | 'custom'>(
    'success@simulator.amazonses.com',
  );
  const [customRecipient, setCustomRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [events, setEvents] = useState<EventItem[]>([]);
  const messageId = sendMutation.data?.messageId;
  const recipient = recipientChoice === 'custom' ? customRecipient : recipientChoice;

  useEffect(() => {
    if (!tenants.length || slug) return;
    setSlug(tenants[0].slug);
  }, [slug, tenants]);

  useEffect(() => {
    if (!messageId) return;

    let pollCount = 0;
    let cancelled = false;

    async function poll() {
      const data = (await apiFetch(`/events/by-message/${messageId}`)) as { items: EventItem[] };
      if (!cancelled) {
        setEvents(data.items ?? []);
      }
    }

    void poll();

    const timer = window.setInterval(() => {
      pollCount += 1;
      if (pollCount >= 20) {
        window.clearInterval(timer);
        return;
      }
      void poll();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [messageId]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-zinc-900">Send Test Email</h1>
        <p className="mt-0.5 text-sm text-zinc-500">
          Use SES simulator addresses to verify tenant delivery and reputation events.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <form
          className="space-y-4 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm"
          onSubmit={(event) => {
            event.preventDefault();
            setEvents([]);
            sendMutation.mutate({ slug, to: recipient, subject, body });
          }}
        >
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-zinc-700" htmlFor="send-tenant">
              Tenant
            </label>
            {tenants.length === 0 ? (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 ring-1 ring-amber-200">
                No active tenants. Create a tenant and wait for DNS verification.
              </p>
            ) : (
              <select
                id="send-tenant"
                className={inputClass}
                value={slug}
                onChange={(event) => setSlug(event.target.value)}
              >
                {tenants.map((tenant) => (
                  <option key={tenant.slug} value={tenant.slug}>
                    {tenant.displayName} ({tenant.slug})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-zinc-700" htmlFor="send-recipient">
              Recipient
            </label>
            <select
              id="send-recipient"
              className={inputClass}
              value={recipientChoice}
              onChange={(event) =>
                setRecipientChoice(event.target.value as (typeof RECIPIENT_OPTIONS)[number] | 'custom')
              }
            >
              {RECIPIENT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
              <option value="custom">Custom…</option>
            </select>
          </div>

          {recipientChoice === 'custom' ? (
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-zinc-700" htmlFor="send-custom-recipient">
                Custom Recipient
              </label>
              <input
                id="send-custom-recipient"
                className={inputClass}
                value={customRecipient}
                onChange={(event) => setCustomRecipient(event.target.value)}
              />
            </div>
          ) : null}

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-zinc-700" htmlFor="send-subject">
              Subject
            </label>
            <input
              id="send-subject"
              className={inputClass}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-zinc-700" htmlFor="send-body">
              Body
            </label>
            <textarea
              id="send-body"
              className={`${inputClass} min-h-28 resize-y`}
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          </div>

          <button
            className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
            type="submit"
            disabled={!tenants.length || sendMutation.isPending}
          >
            {sendMutation.isPending ? 'Sending…' : 'Send Test'}
          </button>

          {sendMutation.isError ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">Send failed.</p>
          ) : null}
        </form>

        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Message Timeline</h2>
          {messageId ? (
            <p className="truncate font-mono text-xs text-zinc-400">{messageId}</p>
          ) : null}
          {events.length ? (
            <div className="space-y-2">
              {events.map((event) => (
                <div
                  key={`${event.eventType}-${event.receivedAt}`}
                  className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm"
                >
                  <span
                    className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      EVENT_COLORS[event.eventType] ?? 'bg-zinc-100 text-zinc-600'
                    }`}
                  >
                    {event.eventType}
                  </span>
                  <span className="text-xs tabular-nums text-zinc-400">{event.receivedAt}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-zinc-200 px-4 py-8 text-center text-sm text-zinc-400">
              {sendMutation.isSuccess ? 'Waiting for events…' : 'Events will appear here after sending.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
